import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import {
  PromptTabs,
  usePromptFilterLocalizations,
} from "@1js/pl-card-components";
import { makeStyles, tokens } from "@fluentui/react-components";
import * as React from "react";
import type { SearchResultsData } from "../../types";
import {
  AgentsPromptTitle,
  AllPromptsTitle,
  LikedPromptTitle,
  MSPromptTitle,
  SavedPromptTitle,
  SearchPromptsTitle,
  SearchResultsTitle,
  TeamsPromptTitle,
  WorkplacePromptsTitle,
} from "./PromptHeadingV2.string";
import { NoWorkplaceResultHeadingParagraph } from "./WorkplacePrompts/WorkPlace.strings";

type PromptHeadingV2Props = {
  navItem: string;
  searchResultsData: SearchResultsData | undefined;
  isMetaOsV2HomePage?: boolean;
  titleOverride?: string;
};

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    textAlign: "left",
    marginTop: "24px",
    marginBottom: "24px",
  },
  heading: {
    color: tokens.colorNeutralForeground1,
    fontSize: "28px",
    fontStyle: "normal",
    fontWeight: "600",
    fontFamily: "Segoe UI",
    lineHeight: "36px",
    alignSelf: "stretch",
    marginBottom: "12px",
  },

  subHeading: {
    color: tokens.colorNeutralForeground1,
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    fontFamily: "Segoe UI",
    lineHeight: "20px",
    alignSelf: "stretch",
    marginBottom: "12px",
  },

  description: {
    color: tokens.colorNeutralForeground1,
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    fontFamily: "Segoe UI",
    lineHeight: "20px",
    width: "850px",
    marginBottom: "12px",
  },

  headingColumn: {
    marginBottom: "12px",
  },
});

export const PromptHeadingV2: React.FC<PromptHeadingV2Props> = ({
  navItem,
  searchResultsData,
  isMetaOsV2HomePage,
  titleOverride,
}) => {
  const styles = useStyles();
  const { facetValueToLabel } = usePromptFilterLocalizations();
  const localizations = useStrings({
    MSPromptTitle,
    SavedPromptTitle,
    TeamsPromptTitle,
    LikedPromptTitle,
    AgentsPromptTitle,
    SearchPromptsTitle,
    WorkplacePromptsTitle,
    AllPromptsTitle,
    NoWorkplaceResultHeadingParagraph,
  });
  const localizationsWithPlaceHolders = useStringsWithPlaceholders({
    SearchResultsTitle,
  });

  const getTitle = () => {
    if (titleOverride !== undefined) {
      return facetValueToLabel(titleOverride);
    }

    switch (navItem) {
      case PromptTabs.MicrosoftPrompts:
        return localizations.MSPromptTitle();
      case PromptTabs.SavedPrompts:
        return localizations.SavedPromptTitle();
      case PromptTabs.GroupPrompts:
        return localizations.TeamsPromptTitle();
      case PromptTabs.LikedPrompts:
        return localizations.LikedPromptTitle();
      case PromptTabs.AgentPrompts:
        return localizations.AgentsPromptTitle();
      case PromptTabs.SearchPrompts:
        return localizations.SearchPromptsTitle();
      case PromptTabs.WorkplacePrompts:
        return localizations.WorkplacePromptsTitle();
      case PromptTabs.AllPrompts:
        return localizations.AllPromptsTitle();
      default:
        return "";
    }
  };

  const getSubtitle = (searchResultsData: SearchResultsData | undefined) => {
    if (
      navItem !== PromptTabs.WorkplacePrompts &&
      searchResultsData &&
      searchResultsData.queryText
    ) {
      return localizationsWithPlaceHolders.SearchResultsTitle({
        queryText: searchResultsData.queryText,
        numOfResults: searchResultsData.numOfResults.toString(),
      });
    } else if (navItem === PromptTabs.WorkplacePrompts) {
      return localizations.NoWorkplaceResultHeadingParagraph();
    } else {
      return "";
    }
  };

  const getContent = () => {
    if (!isMetaOsV2HomePage) {
      return (
        <div className={styles.headingContainer}>
          <h1 className={styles.heading}> {getTitle()}</h1>
          <div className={styles.subHeading}>
            {getSubtitle(searchResultsData)}
          </div>
        </div>
      );
    } else {
      return <></>;
    }
  };

  return getContent();
};
